import type { ReactNode } from "react";

import { type Row, type Table, flexRender } from "@tanstack/react-table";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { DataTableHeader } from "./table-header";

const ALIGN = { start: "text-left", end: "text-right", center: "text-center" } as const;

/** Kengligi berilmagan ustun — shu qadar (px). */
export const DEFAULT_COLUMN_WIDTH = 160;

/** Skeleton qatorlari — sahifa hajmicha, lekin ekrandan oshmaydi. */
const MAX_SKELETON_ROWS = 8;

interface DataTableProps<TData> {
  table: Table<TData>;
  /** Birinchi yuklanish — ma'lumot hali yo'q. */
  isLoading: boolean;
  /** Ma'lumot turibdi, yangisi kelyapti (sahifa yoki filtr almashdi). */
  isFetching?: boolean;
  /** So'rov xatosi: bo'sh ro'yxat bilan yiqilgan so'rov bir xil ko'rinmasin. */
  error?: unknown;
  onRetry?: () => void;
  /** Bo'sh holat — sabab va keyingi qadam bilan. */
  empty: ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  /**
   * Bosiladigan qatorning skrinrider nomi ("… ni ochish"). Berilsa qator
   * tugma sifatida e'lon qilinadi — sichqonchasiz odam ham yozuvni ochadi.
   */
  rowLabel?: (row: Row<TData>) => string;
  /** Qatorga qo'shimcha klass — masalan, xato yetkazishni ajratish. */
  rowClassName?: (row: Row<TData>) => string | undefined;
  className?: string;
}

/**
 * Ma'lumot jadvali (learn-up-admin `ReactTable` asosida).
 *
 * KARTASIZ: jadval sahifa fonida turadi — sarlavha yumaloq "lenta", qatorlar
 * ingichka chiziq bilan ajraladi. Karta ichidagi karta (sahifa → karta →
 * jadval) ko'zni qutilarga bo'lib yuborardi; ro'yxat sahifaning o'zi.
 *
 * Jadval faqat chizadi: filtr, saralash, ko'rinish va sahifa holati sahifada
 * (URL va `localStorage`), bu yerga tayyor `table` bo'lib keladi.
 */
export function DataTable<TData>({
  table,
  isLoading,
  isFetching = false,
  error,
  onRetry,
  empty,
  onRowClick,
  rowLabel,
  rowClassName,
  className,
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const columns = table.getVisibleLeafColumns();
  const rows = table.getRowModel().rows;
  const hasError = !isLoading && error !== undefined && error !== null;
  const isEmpty = !isLoading && !hasError && rows.length === 0;

  /* Ustun kengliklari QAT'IY (`table-fixed` + `colgroup`): brauzer ularni
     katak matnidan hisoblamaydi, shuning uchun sahifa almashganda yoki
     natija bo'sh chiqqanda ustunlar sakramaydi. Jadvalning eng kichik
     kengligi — ustunlar yig'indisi: undan tor joyda jadval siqilmaydi,
     x o'qi bo'yicha aylanadi; kengroq joyda esa ulush bo'yicha cho'ziladi. */
  const widths = columns.map(column => column.columnDef.meta?.width ?? DEFAULT_COLUMN_WIDTH);
  const minWidth = widths.reduce((sum, width) => sum + width, 0);

  const skeletonRows = Math.min(table.getState().pagination.pageSize, MAX_SKELETON_ROWS);

  return (
    // Jadval sahifa bergan balandlikni to'liq egallaydi (`flex-1`) va o'zi
    // aylanadi: sarlavha tepada yopishadi, sahifalash esa jadvaldan keyin —
    // pastki chekkada qoladi. `min-h-64` — past ekranda jadval nolga
    // siqilmasin, o'shanda sahifaning o'zi aylanadi.
    <div className={cn("relative flex min-h-64 flex-1 flex-col", className)}>
      <div className="thin-scrollbar min-h-0 flex-1 overflow-auto">
        <table
          className="w-full table-fixed border-separate border-spacing-0 text-sm"
          style={{ minWidth }}
        >
          <colgroup>
            {columns.map((column, index) => (
              <col key={column.id} style={{ width: widths[index] }} />
            ))}
          </colgroup>
          <DataTableHeader table={table} />
          <tbody aria-busy={isLoading || isFetching}>
            {isLoading
              ? Array.from({ length: skeletonRows }, (_, index) => (
                  <tr key={`skeleton-${index}`}>
                    {columns.map(column => (
                      <td key={column.id} className="border-border/60 border-b px-3 py-3.5">
                        <Skeleton
                          className="h-4 rounded-md"
                          style={{ width: `${55 + ((index * 17 + column.id.length * 7) % 40)}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {!isLoading && !hasError
              ? rows.map((row, index) => (
                  <motion.tr
                    key={row.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.35,
                      delay: Math.min(index, 12) * 0.03,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    role={rowLabel ? "button" : undefined}
                    aria-label={rowLabel?.(row)}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={event => {
                      // Qator ichidagi tugma yoki havolaning Enter'i qatorni ochmasin.
                      if (event.target !== event.currentTarget || !onRowClick) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }}
                    className={cn(
                      "group/row relative transition-colors outline-none",
                      "hover:bg-primary/[0.035] focus-visible:bg-primary/[0.06]",
                      onRowClick && "cursor-pointer",
                      rowClassName?.(row),
                    )}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const meta = cell.column.columnDef.meta;
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            "border-border/60 overflow-hidden border-b px-3 py-3 align-middle",
                            ALIGN[meta?.align ?? "start"],
                            // Chap chekkadagi urg'u chizig'i — qator ustida turilganda.
                            cellIndex === 0 &&
                              "before:bg-primary relative before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:scale-y-0 before:rounded-full before:transition-transform group-hover/row:before:scale-y-100 group-focus-visible/row:before:scale-y-100",
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </motion.tr>
                ))
              : null}
          </tbody>
        </table>
      </div>

      {/* Qayta yuklanish chizig'i sarlavha ostida: eski qatorlar ko'rinib
          turadi (sakrash yo'q), lekin yangilanayotgani sezilsin. */}
      {isFetching && !isLoading ? (
        <div
          role="progressbar"
          aria-label={t("common.loading")}
          className="pointer-events-none absolute inset-x-0 top-10 h-0.5 overflow-hidden"
        >
          <div className="bg-primary animate-indeterminate h-full w-1/2 rounded-full" />
        </div>
      ) : null}

      {hasError || isEmpty ? (
        <div className="absolute inset-x-0 top-10 bottom-0 grid place-items-center overflow-y-auto">
          {hasError ? <ErrorState error={error} onRetry={onRetry} /> : empty}
        </div>
      ) : null}
    </div>
  );
}
